import { z } from 'zod';
import { IdSchema, IsoDateTimeSchema, nullableText } from './common.js';
import { SupplyTypeSchema } from './enums.js';
import { MoneySchema } from './money.js';
/**
 * Excel/CSV importi.
 *
 * IKKI BOSQICH: fayl yuklanadi va TEKSHIRILADI, keyin odam natijani
 * ko'rib turib qo'llaydi. Bir bosqichli import 300 ta tovarni bir
 * bosishda buzib qo'yishi mumkin va orqaga qaytarib bo'lmaydi.
 */
export const IMPORT_STATUSES = ['VALIDATED', 'APPLIED', 'FAILED'];
export const ImportStatusSchema = z.enum(IMPORT_STATUSES);
/**
 * Ustun kalitlari — fayldagi sarlavha SHULARGA moslanadi.
 *
 * `sku` yagona MAJBURIY ustun: u upsert kaliti. Qolganlari bo'lmasa
 * o'sha maydon tegilmaydi (mavjud tovarda) yoki standart qiymat
 * qo'yiladi (yangi tovarda).
 */
export const IMPORT_COLUMNS = [
    'sku',
    'nameUz',
    'nameRu',
    'brand',
    'category',
    'price',
    'oldPrice',
    'costPrice',
    'comingSoon',
    'supplyType',
    'leadTimeDays',
    'searchTags',
    'oemCodes',
    'fitsUz',
    'fitsRu',
    'descUz',
    'descRu',
];
export const ImportColumnSchema = z.enum(IMPORT_COLUMNS);
/**
 * Sarlavha nomlarining muqobillari.
 *
 * Ta'minotchining fayli bizning inglizcha kalitlarimiz bilan kelmaydi —
 * u "Артикул" yoki "Narxi" deb yozadi. Ro'yxatsiz har import qo'lda
 * ustun moslashtirishni talab qilardi.
 *
 * Taqqoslash kichik harfda va probelsiz: "Eski narx" -> "eskinarx".
 */
export const IMPORT_COLUMN_ALIASES = {
    sku: ['sku', 'artikul', 'артикул', 'kod', 'код', 'code'],
    nameUz: ['nameuz', 'nomi', 'nom', 'name', 'naimenovanie', 'наименование', 'название'],
    nameRu: ['nameru', 'nomiru', 'названиеru', 'наименованиеru'],
    brand: ['brand', 'brend', 'бренд', 'ishlabchiqaruvchi', 'производитель'],
    category: ['category', 'kategoriya', 'категория', 'guruh', 'группа'],
    price: ['price', 'narx', 'narxi', 'цена', 'sotuvnarxi'],
    oldPrice: ['oldprice', 'eskinarx', 'стараяцена', 'скидканарх'],
    costPrice: ['costprice', 'tannarx', 'себестоимость', 'закупка'],
    comingSoon: ['comingsoon', 'tezkunda', 'скоро', 'скоровпродаже'],
    supplyType: ['supplytype', 'taminot', 'таминот', 'типпоставки'],
    leadTimeDays: ['leadtimedays', 'kutishmuddati', 'muddat', 'срок', 'срокдней'],
    searchTags: ['searchtags', 'qidiruvteglari', 'teglar', 'теги'],
    oemCodes: ['oemcodes', 'oem', 'oemraqamlar', 'oemкоды', 'аналоги'],
    fitsUz: ['fitsuz', 'moslik', 'mos'],
    fitsRu: ['fitsru', 'совместимость'],
    descUz: ['descuz', 'tavsif', 'tavsifuz'],
    descRu: ['descru', 'описание'],
};
/**
 * "Tez kunda" katagi. Bo'sh — `null` (tegilmaydi). Tanilmagan qiymat — `'invalid'`:
 * jimgina `false` deb qabul qilinsa narxsiz tovar tasodifan sotuvga chiqib ketardi.
 */
export function parseComingSoon(raw) {
    const value = raw
        .trim()
        .toLowerCase()
        .replace(/['‘’ʻʼ`]/gu, '');
    if (value === '')
        return null;
    if (['ha', 'yes', 'true', '1', '+', 'да', 'tez kunda', 'скоро'].includes(value))
        return true;
    if (['yoq', 'no', 'false', '0', '-', 'нет'].includes(value))
        return false;
    return 'invalid';
}
/** Sarlavhani solishtirish uchun normal shakl. */
export function normalizeHeader(value) {
    return value
        .toLowerCase()
        .replace(/[\s_\-.]/gu, '')
        .replace(/['‘’ʻʼ`]/gu, '');
}
/** Sarlavha matnidan ustun kalitini topadi. Topilmasa `null`. */
export function matchColumn(header) {
    const normalized = normalizeHeader(header);
    if (normalized === '')
        return null;
    for (const column of IMPORT_COLUMNS) {
        if (IMPORT_COLUMN_ALIASES[column].includes(normalized))
            return column;
    }
    return null;
}
/**
 * Qoldiq ustunlari do'konga bog'lanadi: "Qoldiq Toshkent", "Остаток Урганч".
 *
 * Do'konlar ma'lumotnomadan keladi, shuning uchun ustun nomi ro'yxatda
 * oldindan berilmaydi: prefiks + do'kon kodi yoki nomi yetarli.
 * Prefiksdan keyin hech narsa yo'q bo'lsa ("Qoldiq") va do'kon bitta bo'lsa
 * — o'sha do'kon; bir nechta bo'lsa noaniq, shuning uchun xato.
 */
export const IMPORT_STOCK_PREFIXES = ['qoldiq', 'stock', 'остаток', 'ostatok'];
/** Do'kon nomi/kodini solishtirish uchun normal shakl. */
export function normalizeShopKey(value) {
    return normalizeHeader(value).replace(/[:()/,;]/gu, '');
}
/**
 * Sarlavha qoldiq ustunimi? Ha bo'lsa — prefiksdan keyingi qism
 * (bo'sh bo'lishi mumkin), aks holda `null`.
 */
export function matchStockHeader(header) {
    const normalized = normalizeShopKey(header);
    for (const prefix of IMPORT_STOCK_PREFIXES) {
        if (normalized.startsWith(prefix))
            return { rest: normalized.slice(prefix.length) };
    }
    return null;
}
/** Bitta qatordagi xato — qator raqami bilan, chunki odam faylda tuzatadi. */
export const ImportRowErrorSchema = z.object({
    /** Fayldagi qator raqami (1 — sarlavha), Excel'dagi bilan bir xil. */
    row: z.number().int().positive(),
    column: z.string().nullable(),
    messageUz: z.string(),
    messageRu: z.string(),
    /** Odam faylda topishi uchun — nima yozilgan edi. */
    value: nullableText(120),
});
/** Tekshiruvdan o'tgan qator — `apply` bosqichida shu ishlatiladi. */
export const ImportRowPlanSchema = z.object({
    row: z.number().int().positive(),
    sku: z.string(),
    /** `true` — yangi tovar yaratiladi (`DRAFT` bo'lib). */
    isNew: z.boolean(),
    productId: IdSchema.nullable(),
    nameUz: z.string().nullable(),
    nameRu: z.string().nullable(),
    brandId: IdSchema.nullable(),
    categoryId: IdSchema.nullable(),
    price: MoneySchema.nullable(),
    oldPrice: MoneySchema.nullable(),
    costPrice: MoneySchema.nullable(),
    /** Faylda "Tez kunda" ustuni bo'lmasa yoki katak bo'sh bo'lsa `null` — tegilmaydi. */
    comingSoon: z.boolean().nullable().default(null),
    supplyType: SupplyTypeSchema.nullable(),
    leadTimeDays: z.number().int().nonnegative().nullable(),
    searchTags: z.string().nullable(),
    oemCodes: z.string().nullable(),
    fitsUz: z.string().nullable(),
    fitsRu: z.string().nullable(),
    descUz: z.string().nullable(),
    descRu: z.string().nullable(),
    /**
     * Do'konlar bo'yicha qoldiq (ABSOLYUT son, o'zgarish emas). Faqat faylda
     * berilgan do'konlar; bo'sh katak — tegilmaydi. Eski (qoldiqsiz) importlar
     * uchun standart — bo'sh ro'yxat.
     */
    stock: z.array(z.object({ shopId: IdSchema, qty: z.number().int().nonnegative() })).default([]),
});
export const ImportRunResponseSchema = z.object({
    id: IdSchema,
    fileName: z.string(),
    status: ImportStatusSchema,
    totalRows: z.number().int().nonnegative(),
    createRows: z.number().int().nonnegative(),
    updateRows: z.number().int().nonnegative(),
    errorRows: z.number().int().nonnegative(),
    /** Qaysi ustunlar tanildi — odam moslash to'g'ri bo'lganini ko'rsin. */
    columns: z.array(ImportColumnSchema),
    /** Tanilmagan sarlavhalar — jimgina tashlab yuborilmaydi. */
    ignoredColumns: z.array(z.string()),
    /** Qoldiq ustunlari qaysi do'konlarga moslandi (do'kon nomi bilan). */
    stockShops: z.array(z.string()).default([]),
    errors: z.array(ImportRowErrorSchema),
    /** Ko'rib chiqish uchun birinchi qatorlar. Hammasi emas. */
    preview: z.array(ImportRowPlanSchema),
    createdById: IdSchema,
    appliedAt: IsoDateTimeSchema.nullable(),
    createdAt: IsoDateTimeSchema,
});
/** Yangi tovar HAR DOIM `DRAFT` bo'lib tug'iladi (docs/04-REJA.md). */
export const IMPORT_NEW_PRODUCT_STATUS = 'DRAFT';
/** Bitta importda ruxsat etilgan eng ko'p qator. */
export const IMPORT_MAX_ROWS = 5000;
//# sourceMappingURL=import.js.map